/* eslint-disable @typescript-eslint/no-empty-function */
import type React from "react";
import { forwardRef, useEffect, useState } from "react";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { SvgIconComponent } from "@mui/icons-material";
import type { ChangeHandler } from "react-hook-form";
import styled from "styled-components";

import { StyledInput } from "shared/atoms/inputs/StyledInputs";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 4px;
  height: 40px;
  width: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  & svg {
    max-width: 25px;
  }
`;

const SearchInput = styled(StyledInput)<{ $icon: boolean; $search: boolean }>`
  box-sizing: border-box;
  padding: ${props => (props.$icon ? `0px 40px 0px 15px` : `0px 15px`)};
`;

interface Props {
  placeholder?: string;
  icon?: React.FunctionComponent<React.SVGAttributes<SVGElement>> | SvgIconComponent;
  name: string;
  className?: string;
  reset?: () => void;
  onChange?: ChangeHandler;
  onBlur?: ChangeHandler;
  search?: string;
  inputStyle?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  initialValue?: string;
}

export const Search = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    className = undefined,
    placeholder = "",
    name,
    icon: Icon = null,
    reset = () => {},
    search = null,
    onChange = undefined,
    onBlur = undefined,
    inputStyle,
    wrapperStyle,
    initialValue = "",
  } = props;
  const [searching, setSearching] = useState({ active: false, value: initialValue });

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setSearching({ active: true, value: target?.value });
  };

  const handleOut = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setSearching({ active: e.type === "focus" || target?.value !== "", value: target?.value });
  };

  useEffect(() => {
    const selector = document.querySelector(`input[name="${name}"]`);
    selector?.addEventListener("focus", handleOut);
    selector?.addEventListener("blur", handleOut);
    selector?.addEventListener("input", handleChange);

    return () => {
      selector?.removeEventListener("input", handleChange);
      selector?.removeEventListener("focus", handleOut);
      selector?.removeEventListener("blur", handleOut);
    };
  }, [name]);

  useEffect(() => {
    if (search === "") {
      setSearching({ active: false, value: "" });
    }
  }, [search]);

  return (
    <Wrapper data-testid="search-wrapper" style={wrapperStyle}>
      {Icon && !searching.value && (
        <IconWrapper>
          <Icon />
        </IconWrapper>
      )}
      <SearchInput
        style={inputStyle}
        className={className}
        ref={ref}
        name={name}
        $icon={!!Icon}
        $search={searching.active}
        onChange={onChange}
        onBlur={onBlur}
        defaultValue={initialValue}
        placeholder={placeholder}
      />
      {!!searching.value && (
        <IconWrapper
          onClick={() => {
            setSearching({ active: false, value: "" });
            reset();
          }}
          data-testid="reset-search"
        >
          <FontAwesomeIcon icon={faTimes} size="sm" />
        </IconWrapper>
      )}
    </Wrapper>
  );
});
